import apiClient from './client';
import type {
  LoginRequest,
  LoginResponse,
  User,
  AuthProvider,
} from '../types';
import {
  clearStoredRefreshToken,
  storeRefreshTokenIfNative,
} from '@pp/shared/auth/refreshTokenStorage';

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  company?: string;
  phoneNumber?: string;
}

/**
 * Login with email and password (global login - works across all tenants)
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/login-global', credentials);
  // Native clients: backend includes the refresh token in the body; persist it
  // so /auth/refresh can read it back via X-Refresh-Token next launch.
  // No-op on web — web clients get the HttpOnly cookie instead.
  await storeRefreshTokenIfNative(response.data.refreshToken);
  return response.data;
}

/**
 * Register a new user account
 */
export async function register(data: RegisterRequest): Promise<void> {
  await apiClient.post('/auth/register', data);
}

/**
 * Get current user from token
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

/**
 * Refresh JWT token
 */
export async function refreshToken(): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/refresh');
  // Backend rotates the refresh token on every refresh; persist the new one on native.
  await storeRefreshTokenIfNative(response.data.refreshToken);
  return response.data;
}

/**
 * Logout (invalidate token)
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
  // Drop the stored refresh token on native. No-op on web (HttpOnly cookie is
  // cleared by the server's response to /logout).
  await clearStoredRefreshToken();
}

/**
 * Get available auth providers for current tenant
 */
export async function getAuthProviders(): Promise<AuthProvider[]> {
  const response = await apiClient.get<AuthProvider[]>('/auth/providers');
  return response.data;
}

/**
 * Request password reset
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient.post('/auth/forgot-password', { email });
}

/**
 * Reset password with token
 */
export async function resetPassword(email: string, token: string, newPassword: string): Promise<void> {
  await apiClient.post('/auth/reset-password', { email, token, newPassword });
}

/**
 * Confirm email address with token
 */
export async function confirmEmail(email: string, token: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/confirm-email', { email, token });
  return response.data;
}

/**
 * Tenant info returned from email lookup
 */
export interface TenantInfo {
  subdomain: string;
  companyName: string;
}

/**
 * Response from tenant lookup by email
 */
export interface TenantLookupResponse {
  tenants: TenantInfo[];
}

/**
 * Look up which tenant(s) a user's email belongs to.
 * This is a global endpoint that doesn't require tenant context.
 */
export async function lookupTenantsByEmail(email: string): Promise<TenantLookupResponse> {
  const response = await apiClient.post<TenantLookupResponse>('/auth/lookup-tenant', { email });
  return response.data;
}

/**
 * Response from Google authorize endpoint
 */
export interface GoogleAuthorizeResponse {
  authorizationUrl: string;
  state: string;
}

/**
 * Request for Google callback endpoint
 */
export interface GoogleCallbackRequest {
  code: string;
  state: string;
  redirectUri: string;
}

/**
 * Response when Google account linking is required (existing account with same email)
 */
export interface GoogleLinkingRequiredResponse {
  requiresLinking: true;
  linkingToken: string;
  email: string;
  message: string;
  expiresAt: string;
}

/**
 * Request to complete Google account linking with password
 */
export interface GoogleLinkingRequest {
  linkingToken: string;
  password: string;
}

/**
 * Type guard to check if response requires linking
 */
export function isLinkingRequired(
  response: LoginResponse | GoogleLinkingRequiredResponse
): response is GoogleLinkingRequiredResponse {
  return 'requiresLinking' in response && response.requiresLinking === true;
}

/**
 * Get Google OAuth authorization URL
 * @param redirectUri The URI to redirect to after Google authentication
 * @param tenantId Optional tenant ID to associate with the login
 */
export async function getGoogleAuthorizeUrl(
  redirectUri: string,
  tenantId?: string
): Promise<GoogleAuthorizeResponse> {
  const params = new URLSearchParams({ redirectUri });
  if (tenantId) {
    params.append('tenantId', tenantId);
  }
  const response = await apiClient.get<GoogleAuthorizeResponse>(
    `/auth/google/authorize?${params.toString()}`
  );
  return response.data;
}

/**
 * Complete Google OAuth login by exchanging authorization code for tokens
 * @param code Authorization code from Google
 * @param state State parameter for CSRF validation
 * @param redirectUri The redirect URI used in the authorization request
 * @returns LoginResponse if successful, or GoogleLinkingRequiredResponse if account linking is needed
 */
export async function googleCallback(
  code: string,
  state: string,
  redirectUri: string
): Promise<LoginResponse | GoogleLinkingRequiredResponse> {
  const response = await apiClient.post<LoginResponse | GoogleLinkingRequiredResponse>('/auth/google/callback', {
    code,
    state,
    redirectUri,
  });
  // If this completed login (vs. requiring account linking), persist any refresh
  // token the backend included for native clients.
  if (!('requiresLinking' in response.data)) {
    await storeRefreshTokenIfNative((response.data as LoginResponse).refreshToken);
  }
  return response.data;
}

/**
 * Complete Google account linking by verifying password
 * Called after googleCallback returns a linking required response
 * @param linkingToken The token from the linking required response
 * @param password The user's PacketProspector account password (NOT their Google password)
 */
export async function completeGoogleLinking(
  linkingToken: string,
  password: string
): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/auth/google/link', {
    linkingToken,
    password,
  });
  await storeRefreshTokenIfNative(response.data.refreshToken);
  return response.data;
}

/**
 * External login provider types
 */
export type AuthProviderType = 'Local' | 'Google' | 'AzureAD' | 'Microsoft' | 'SAML' | 'Apple';

/**
 * Information about a linked external login
 */
export interface ExternalLoginInfo {
  providerType: AuthProviderType;
  providerName: string;
  externalEmail: string;
  linkedAt: string;
}

/**
 * Response containing the user's linked external logins
 */
export interface ExternalLoginsResponse {
  externalLogins: ExternalLoginInfo[];
  hasPassword: boolean;
  authProvider: AuthProviderType;
}

/**
 * Get current user's linked external logins
 */
export async function getExternalLogins(): Promise<ExternalLoginsResponse> {
  const response = await apiClient.get<ExternalLoginsResponse>('/auth/me/external-logins');
  return response.data;
}

/**
 * Get Google OAuth authorization URL for linking to existing account
 * @param redirectUri The URI to redirect to after Google authentication
 */
export async function getGoogleLinkAuthorizeUrl(
  redirectUri: string
): Promise<GoogleAuthorizeResponse> {
  const params = new URLSearchParams({ redirectUri });
  const response = await apiClient.get<GoogleAuthorizeResponse>(
    `/auth/link/google/authorize?${params.toString()}`
  );
  return response.data;
}

/**
 * Complete Google OAuth account linking
 * @param code Authorization code from Google
 * @param state State parameter for CSRF validation
 * @param redirectUri The redirect URI used in the authorization request
 */
export async function googleLinkCallback(
  code: string,
  state: string,
  redirectUri: string
): Promise<ExternalLoginsResponse> {
  const response = await apiClient.post<ExternalLoginsResponse>('/auth/link/google/callback', {
    code,
    state,
    redirectUri,
  });
  return response.data;
}

/**
 * Link Sign in with Apple to the signed-in account (Profile flow). The
 * caller runs the native Apple sheet while authenticated and posts the
 * fresh identity token; the backend verifies it against Apple's JWKS.
 */
export async function linkAppleAccount(
  identityToken: string,
  nonce?: string,
): Promise<ExternalLoginsResponse> {
  const response = await apiClient.post<ExternalLoginsResponse>(
    '/auth/me/external-logins/link-apple',
    { identityToken, nonce },
  );
  return response.data;
}

/**
 * Unlink an external login from the current user's account
 * @param providerType The provider type to unlink
 */
export async function unlinkExternalLogin(
  providerType: AuthProviderType
): Promise<ExternalLoginsResponse> {
  const response = await apiClient.post<ExternalLoginsResponse>('/auth/me/external-logins/unlink', {
    providerType,
  });
  return response.data;
}

// ==================== New ISP Registration ====================

export interface RegisterNewISPRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  companyName: string;
  subdomain: string;
  selectedPlanId: string;
  billingInterval: 'Monthly' | 'Annual';
  /** Operations only */
  billingAddOn?: boolean;
  customerPortalAddOn?: boolean;
}

export interface RegisterNewISPResponse {
  message: string;
  checkoutUrl: string;
  tenantId: string;
  userId: string;
  requiresApproval: boolean;
  approvalMessage?: string;
}

export interface SubdomainAvailabilityResponse {
  available: boolean;
}

export interface VerifyCheckoutResponse {
  success: boolean;
  tenantId: string;
  subscriptionCreated: boolean;
  message: string;
}

/**
 * Register a new ISP tenant with credit card requirement
 */
export async function registerNewISP(data: RegisterNewISPRequest): Promise<RegisterNewISPResponse> {
  const response = await apiClient.post<RegisterNewISPResponse>('/auth/register-new-isp', data);
  return response.data;
}

/**
 * Check if a subdomain is available
 */
export async function checkSubdomainAvailability(subdomain: string): Promise<SubdomainAvailabilityResponse> {
  const response = await apiClient.get<SubdomainAvailabilityResponse>(
    `/auth/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`
  );
  return response.data;
}

/**
 * Verify Stripe checkout session completed successfully
 */
export async function verifyCheckoutSession(sessionId: string): Promise<VerifyCheckoutResponse> {
  const response = await apiClient.get<VerifyCheckoutResponse>(
    `/auth/verify-checkout?session_id=${encodeURIComponent(sessionId)}`
  );
  return response.data;
}

// ===========================================================================
// SSO (single sign-on) — Google + Apple, shared by web JS SDK and native
// iOS/Android Capacitor plugins.
//
// Same backend endpoints power both surfaces. The caller is responsible for
// obtaining an ID token from the appropriate SDK and sending it here; the
// server verifies the token's signature against the provider's JWKs.
// ===========================================================================

export interface SsoTenantSelectionRequiredResponse {
  email: string;
  tenants: Array<{ subdomain: string; companyName: string }>;
}

/**
 * Returned when the verified Google/Apple identity has no PacketProspector
 * account to sign into (accounts are invite-only; Apple's Hide My Email
 * relay addresses never match a provisioned work email). The client should
 * offer a one-time "sign in with your work email to link" screen, then
 * redeem pendingLinkToken via completePendingSsoLink() once authenticated.
 */
export interface SsoLinkingRequiredResponse {
  linkingRequired: true;
  /** Provider name as the server spells it: 'Google' | 'Apple'. */
  provider: string;
  /** Provider-asserted email — display only, may be a private relay. */
  email: string;
  /** Opaque short-lived token wrapping the verified external identity. */
  pendingLinkToken: string;
}

export type SsoLoginResult =
  | LoginResponse
  | SsoTenantSelectionRequiredResponse
  | SsoLinkingRequiredResponse;

/**
 * Sign in with a Google ID token already obtained from a Google SDK (web JS
 * SDK on the login page; @capgo/capacitor-social-login inside the native
 * shell). Returns a standard LoginResponse on success, OR a tenant-selection
 * response if the verified email exists in multiple tenants. In that case
 * the caller should prompt the user to pick and re-submit with the chosen
 * tenantSubdomain.
 */
export async function googleSsoLogin(
  idToken: string,
  tenantSubdomain?: string,
): Promise<SsoLoginResult> {
  const response = await apiClient.post<SsoLoginResult>(
    '/auth/google-sso',
    { idToken, tenantSubdomain },
  );
  if (!isSsoTenantSelectionRequired(response.data) && !isSsoLinkingRequired(response.data)) {
    await storeRefreshTokenIfNative(response.data.refreshToken);
  }
  return response.data;
}

/**
 * Sign in with an Apple identity token. Apple sends the user's name only on
 * the very first sign-in for a given Apple ID + app pair, so the caller must
 * persist the values locally and re-send them on subsequent sign-ins.
 */
export async function appleSsoLogin(
  identityToken: string,
  opts?: {
    nonce?: string;
    firstName?: string;
    lastName?: string;
    tenantSubdomain?: string;
  },
): Promise<SsoLoginResult> {
  const response = await apiClient.post<SsoLoginResult>(
    '/auth/apple-sso',
    {
      identityToken,
      nonce: opts?.nonce,
      firstName: opts?.firstName,
      lastName: opts?.lastName,
      tenantSubdomain: opts?.tenantSubdomain,
    },
  );
  if (!isSsoTenantSelectionRequired(response.data) && !isSsoLinkingRequired(response.data)) {
    await storeRefreshTokenIfNative(response.data.refreshToken);
  }
  return response.data;
}

export function isSsoTenantSelectionRequired(
  response: SsoLoginResult,
): response is SsoTenantSelectionRequiredResponse {
  return Array.isArray((response as SsoTenantSelectionRequiredResponse).tenants);
}

export function isSsoLinkingRequired(
  response: SsoLoginResult,
): response is SsoLinkingRequiredResponse {
  return (response as SsoLinkingRequiredResponse).linkingRequired === true;
}

/**
 * Redeem a pending SSO link token after the user authenticated with their
 * work-email credentials. Attaches the verified Google/Apple identity to
 * the signed-in account so the NEXT social sign-in matches directly.
 * Requires a valid access token (call after login succeeds).
 */
export async function completePendingSsoLink(pendingLinkToken: string): Promise<void> {
  await apiClient.post('/auth/me/external-logins/link-pending', { pendingLinkToken });
}
